import Styles from './styles.module.css';
import '../../salon/auth/login/tokens.css';
import React, { useContext, useEffect, useRef, useState } from 'react';
import NoIndex from '../../components/NoIndex';
import {
  IoDocumentTextOutline,
  IoPeopleOutline,
  IoStorefrontOutline,
  IoChatbubblesOutline,
  IoCalendarOutline,
  IoHelpCircleOutline,
  IoLogInOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoExpandOutline,
  IoDownloadOutline,
  IoSwapVerticalOutline,
  IoBulbOutline,
  IoArrowUpOutline,
  IoCompassOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';
import { FaRegStar } from 'react-icons/fa6';
import { RiLockPasswordLine } from 'react-icons/ri';

/*
 * 学校管理画面の使い方マニュアル(社内・学校向けに公開するヘルプページ)。
 * ログイン不要で誰でも開けるように school/service の認証ゲートの外にルーティングする(App.js 側)。
 * スマホ・PCどちらでも読めるよう、PCでは左サイドバーに目次を固定し、スマホでは上部のタブ切り替え+
 * ハンバーガーの目次シートに切り替える(school/index.tsx の上部タブと同じ見た目のトークンを使用)。
 *
 * 画面のスクリーンショットは public/school-manual/ に置き、/school-manual/xxx.png で参照する
 * (テスト用の学校アカウント「TEST専門学校」で撮影したもの)。
 */

type Section = {
  id: string;
  title: string;
  icon: React.ReactNode;
};

// 「紙の求人票の取り込み(スキャン)」の案内は、スクリーンショットが揃うまで一旦非表示にする
const SHOW_SCAN_IMPORT = false;

// 画像を拡大表示するためのライトボックスを開く関数。Shot は本文中の色々な場所から
// (Props を毎回渡さず) 呼べるよう、Context 経由で SchoolManual 側の setZoom を受け取る
const ZoomContext = React.createContext<(src: string, alt: string) => void>(() => {});

// 画像のどこをタップすればいいかを示す番号ピン。x/y は画像に対する位置の割合(0-100)で指定する
// ので、縮小表示でも拡大表示でも同じ場所を指せる
type Hotspot = { x: number; y: number; n: number };

// 画面のスクリーンショット1枚。キャプション付きで、枠線のついたカードとして表示する。
// 画像をクリック(タップ)すると、元の解像度に近いサイズで大きく表示する(特に2枚並びの
// スクリーンショットや、画面内の文字が小さいものは、このままだと読めないため)。hotspots を
// 渡すと、本文の手順(①②③)と対応する番号ピンを画像上に重ねて表示する
//
// ── caption を書くかどうかのルール(StepList との重複を防ぐため) ──
// 1. この Shot の直前(同じ SubHeading 内)に StepList があり、かつ hotspots を付けている場合、
//    caption に手順をもう一度書かない。「絞り込み後。」「表示切替後の〜」のように StepList の
//    文言をなぞる書き出しは禁止。caption は省略するか、StepList に書いていない新情報
//    (結果画面に並ぶ項目名、仕様の補足など)だけを書く。
// 2. StepList が無い単独の Shot(操作の起点ではなく結果画面・状態の対比だけを見せるもの)には、
//    画面が何かを示す caption を必ずつける。
// 3. 判断に迷ったら「StepList を声に出して読んだときに、caption と同じ内容を2回言っていないか」
//    を基準にする。
const Shot: React.FC<{ src: string; alt: string; caption?: string; hotspots?: Hotspot[] }> = ({ src, alt, caption, hotspots }) => {
  const openZoom = useContext(ZoomContext);
  return (
    <figure className={Styles.shot}>
      <button type="button" className={Styles.shotButton} onClick={() => openZoom(src, alt)} aria-label={`${alt}を拡大表示`}>
        <img src={src} alt={alt} loading="lazy" />
        {hotspots?.map((h, i) => (
          <span key={`${h.n}-${i}`} className={Styles.hotspot} style={{ left: `${h.x}%`, top: `${h.y}%` }}>{h.n}</span>
        ))}
        <span className={Styles.shotZoomHint}><IoExpandOutline /></span>
      </button>
      {caption && <figcaption className={Styles.shotCaption}>{caption}</figcaption>}
    </figure>
  );
};

// 機能の説明を、文章だけの箇条書きでなく「アイコン + 見出し + 説明」で見せるための1項目。
// アイコンは実際の画面で使われているものと同じ絵柄にして、画面上のどのボタンの説明かが
// 見た目だけでも分かるようにしている
const IconItem: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className={Styles.iconItem}>
    <span className={Styles.iconItemIcon}>{icon}</span>
    <div className={Styles.iconItemBody}>
      <p className={Styles.iconItemTitle}>{title}</p>
      <p className={Styles.iconItemDesc}>{children}</p>
    </div>
  </div>
);

// IconItem をまとめて「機能」として枠で囲む箱。本文の説明文・スクリーンショットと区別がつくよう、
// タイトル付きの枠で視覚的にひとかたまりだと分かるようにする
const FeatureBox: React.FC<{ title?: string; children: React.ReactNode }> = ({ title = '機能', children }) => (
  <div className={Styles.featureBox}>
    <p className={Styles.featureBoxTitle}>{title}</p>
    <div className={Styles.iconList}>{children}</div>
  </div>
);

// 画面の説明だけでなく、実際の業務でどう使うかを示す「活用のヒント」の箱。
// 「この画面には何がある」で終わらず、「だから何に使えるか」まで書くための場所
const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.tipBox}>
    <IoBulbOutline className={Styles.tipBoxIcon} />
    <div className={Styles.tipBoxBody}>
      <p className={Styles.tipBoxTitle}>活用のヒント</p>
      <ul className={Styles.tipBoxList}>{children}</ul>
    </div>
  </div>
);

// 操作手順を①②③の番号付きで見せるための一覧。スクリーンショットそのものに座標で番号を
// 振ると、画面が少し変わるだけでずれてしまうため、文章側に番号を振って操作の順序を伝える
const StepList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ol className={Styles.stepList}>{children}</ol>
);
const Step: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className={Styles.step}>{children}</li>
);

// 操作を間違えやすいポイントの注意書き(黄色)。公欠申請の「重要」(赤)より一段軽い注意で、
// 赤は取り消しにくい操作などの重大な注意だけに使い、黄色と役割を分けている
const Caution: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.caution}>
    <IoAlertCircleOutline className={Styles.cautionIcon} />
    <div>{children}</div>
  </div>
);

// 各セクションの冒頭で「この画面で何ができるか」を1行でまとめる太字のリード文。
// そのあとに続く詳しい説明文とは役割を分け、流し読みでも要点が伝わるようにする
const SectionLead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={Styles.sectionLead}>{children}</p>
);

// セクション内を機能のまとまりごとに分ける小見出し(例: 求人情報セクションを
// 「探す/確認する/管理する」に分割する)。長いセクションでも、必要な操作だけ探せるようにする
const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className={Styles.subHeading}>{children}</h3>
);

const SECTIONS: Section[] = [
  { id: 'intro', title: 'はじめに', icon: <IoHelpCircleOutline /> },
  { id: 'login', title: 'ログインと画面の見方', icon: <IoLogInOutline /> },
  { id: 'recruit', title: '求人情報', icon: <IoDocumentTextOutline /> },
  { id: 'students', title: '学生の進捗情報', icon: <IoPeopleOutline /> },
  { id: 'salon', title: 'サロン分析', icon: <IoStorefrontOutline /> },
  { id: 'guidance', title: 'ガイダンス回答状況', icon: <IoChatbubblesOutline /> },
  { id: 'absence', title: '公欠申請', icon: <IoCalendarOutline /> },
  { id: 'faq', title: 'よくある質問', icon: <IoHelpCircleOutline /> },
];

// 「やりたいことから探す」の導線。機能名を覚えていなくても、目的の文章から該当の章へ
// 直接ジャンプできるようにする(ページ冒頭に並べる)
const GOALS: { label: string; id: string }[] = [
  { label: 'ログインできない', id: 'login' },
  { label: '企業から届いた求人票を確認したい', id: 'recruit' },
  { label: '学生の就活の進み具合を確認したい', id: 'students' },
  { label: '学生の志望先・ガイダンスの回答を確認したい', id: 'guidance' },
  { label: '学生から届いた公欠申請を許可・不許可にしたい', id: 'absence' },
  { label: 'うまくいかない・困っている', id: 'faq' },
];

const SchoolManual: React.FC = () => {
  const [ activeId, setActiveId ] = useState<string>(SECTIONS[0].id);
  const [ menuOpen, setMenuOpen ] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  // クリックで拡大表示中のスクリーンショット(無ければ非表示)
  const [ zoom, setZoom ] = useState<{ src: string; alt: string } | null>(null);
  const openZoom = (src: string, alt: string) => setZoom({ src, alt });

  // 拡大表示中は Esc キーで閉じられるようにする
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoom(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  // スクロール位置に応じて、今読んでいる章の目次項目をハイライトする(スマホ・PC共通)。
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const goTo = (id: string) => {
    setMenuOpen(false);
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ページ上部へ戻るボタン。長いページなので、ある程度スクロールしたら表示する
  const [ showBackToTop, setShowBackToTop ] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <ZoomContext.Provider value={openZoom}>
      <NoIndex title="学校管理画面の使い方 | memorii works" />
      <div className={Styles.page}>
        {/* スマホ用の上部バー(ロゴ代わりの見出し + 目次を開くボタン) */}
        <div className={Styles.mobileBar}>
          <span className={Styles.mobileBarTitle}>学校管理画面マニュアル</span>
          <button
            type="button"
            className={Styles.menuButton}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="目次を開く">
            {menuOpen ? <IoCloseOutline /> : <IoMenuOutline />}
          </button>
        </div>

        <div className={Styles.layout}>
          {/* 目次(PCは左に固定表示。スマホはボタンで開くシート) */}
          <nav className={`${Styles.toc} ${menuOpen ? Styles.toc_open : ''}`}>
            <p className={Styles.tocHeading}>目次</p>
            <ul className={Styles.tocList}>
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={activeId === s.id ? `${Styles.tocItem} ${Styles.tocItem_active}` : Styles.tocItem}
                    onClick={() => goTo(s.id)}>
                    <span className={Styles.tocIcon}>{s.icon}</span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          {menuOpen && <div className={Styles.tocOverlay} onClick={() => setMenuOpen(false)} />}

          {/* 本文 */}
          <main className={Styles.content}>
            <header className={Styles.pageHeader}>
              <h1 className={Styles.pageTitle}>学校管理画面の使い方</h1>
              <p className={Styles.pageLead}>
                学校の就職支援ご担当者向けに、memorii works の学校管理画面の使い方をまとめたマニュアルです。
                <a
                  href="https://memorii-works.com/company/login"
                  target="_blank"
                  rel="noreferrer"
                  className={Styles.inlineCode}>
                  ログイン画面
                </a>
                の「アカウント種別」で「学校関係者様」を選んでログインします。
                パソコン・スマートフォンのどちらからでもご覧いただけます。
              </p>
              <p className={Styles.pageUpdated}>最終更新日：2026年10月14日</p>
            </header>

            {/* やりたいことから探す(機能名を覚えていなくても、目的から該当の章へ飛べる) */}
            <nav className={Styles.goalBox} aria-label="やりたいことから探す">
              <p className={Styles.goalBoxTitle}>
                <IoCompassOutline className={Styles.goalBoxIcon} />
                やりたいことから探す
              </p>
              <div className={Styles.goalList}>
                {GOALS.map((g) => (
                  <button key={g.id} type="button" className={Styles.goalItem} onClick={() => goTo(g.id)}>
                    {g.label}
                  </button>
                ))}
              </div>
            </nav>

            <section
              id="intro"
              ref={(el) => (sectionRefs.current['intro'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHelpCircleOutline className={Styles.sectionIcon} />
                はじめに
              </h2>
              <SectionLead>
                学校管理画面は、サロンから届く求人情報と、学生の就職活動の進み具合を確認するための画面です。
              </SectionLead>
              <p>
                学生が使う memorii works アプリと連動しており、学生がアプリ上で見学予約や応募、ガイダンス回答、
                公欠申請を行うと、その内容が学校管理画面に反映されます。
              </p>

              <SubHeading>はじめて使うときの流れ</SubHeading>
              <StepList>
                <Step>
                  「学校関係者様」としてログインします（
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('login')}>
                    ログインと画面の見方
                  </button>
                  参照）。
                </Step>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('recruit')}>
                    「求人情報」
                  </button>
                  タブで、サロンから届いた求人票を確認します。
                </Step>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('students')}>
                    「学生の進捗情報」
                  </button>
                  タブで、学生の活動状況を確認します。
                </Step>
              </StepList>

              <p>
                画面の上部には、できることごとに分かれたタブが並びます。契約内容や学校の設定によって、表示される
                タブは学校ごとに異なります。たとえば「ガイダンス回答状況」「公欠申請」はそれぞれの機能を利用している
                学校にのみ表示されます。お使いの画面に見当たらないタブがある場合は、運営（memorii works）までお問い合わせください。
              </p>
            </section>

            <section
              id="login"
              ref={(el) => (sectionRefs.current['login'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoLogInOutline className={Styles.sectionIcon} />
                ログインと画面の見方
              </h2>
              <SectionLead>発行されたメールアドレス・パスワードでログインし、上部タブで画面を切り替えます。</SectionLead>
              <p className={Styles.footnote}>
                ※ご利用開始する場合は、事前にお問い合わせいただいたうえで、学校様のアカウントを発行する
                必要がございます。
              </p>

              <StepList>
                <Step>ログイン画面の「アカウント種別」で「学校関係者様」を選びます。</Step>
                <Step>発行されたメールアドレスとパスワードを入力します。</Step>
                <Step>「ログインする」を押します。</Step>
              </StepList>

              <Caution>
                「アカウント種別」が「事業者様」のままだとログインできません。必ず「学校関係者様」を選んでから
                入力してください。
              </Caution>

              <Shot
                src="/school-manual/login.png"
                alt="ログイン画面"
                hotspots={[
                  { n: 1, x: 72, y: 34 },
                  { n: 2, x: 68, y: 48 },
                  { n: 3, x: 68, y: 70 },
                ]} />
            </section>

            <section
              id="recruit"
              ref={(el) => (sectionRefs.current['recruit'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoDocumentTextOutline className={Styles.sectionIcon} />
                求人情報
              </h2>
              <SectionLead>サロンから届いた求人票を確認・絞り込み・ダウンロードできる、基本となるタブです。</SectionLead>

              <SubHeading>求人票を探す</SubHeading>
              <Shot
                src="/school-manual/recruit-list.png"
                alt="求人情報タブの一覧画面"
                caption="求人情報タブ。企業ごとに都道府県・店舗数・求人票などが並ぶ"
                hotspots={[{ n: 1, x: 87, y: 7 }]} />
              <StepList>
                <Step>右上の「検索」ボタンを押します。</Step>
                <Step>卒業時年齢・職種・企業名・都道府県・給与・ハッシュタグなど、条件を入力します。</Step>
                <Step>絞り込まれた一覧が表示されます。条件はタグとして表示され、「検索条件をリセット」でいつでも戻せます。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/school-manual/recruit-filter-modal.png"
                  alt="求人検索の絞り込み条件を指定する画面"
                  caption="絞り込み条件(卒業時年齢・職種・企業名・都道府県・給与 など)"
                  hotspots={[{ n: 2, x: 50, y: 75 }]} />
                <Shot
                  src="/school-manual/recruit-filtered.png"
                  alt="絞り込み後の求人一覧画面"
                  hotspots={[{ n: 3, x: 20, y: 21 }]} />
              </div>

              <SubHeading>求人票の詳細を確認する</SubHeading>
              <StepList>
                <Step>「店舗数」（例: 12店舗）をクリックすると、店舗情報が開きます。</Step>
                <Step>「卒業生」の人数をクリックすると、卒業生情報が開きます。</Step>
              </StepList>
              <Shot
                src="/school-manual/recruit-list.png"
                alt="求人情報タブの一覧画面(店舗数・卒業生の位置)"
                hotspots={[
                  { n: 1, x: 40, y: 37 },
                  { n: 2, x: 65, y: 37 },
                ]} />
              <div className={Styles.shotRow}>
                <Shot
                  src="/school-manual/recruit-store-detail.png"
                  alt="店舗情報の詳細パネル"
                  caption="店舗情報。写真・住所・最寄駅に加え、施術頻度やスタイルの傾向をレーダーチャートで確認できる" />
                <Shot
                  src="/school-manual/recruit-graduates.png"
                  alt="求人の卒業生一覧と卒業生を追加する画面"
                  caption="卒業生情報。サロン側が登録した卒業生に加え、学校側からも卒業生を追加できる(学校の画面にだけ表示され、サロン側には表示されない)" />
              </div>

              <SubHeading>求人票を管理する</SubHeading>
              <FeatureBox>
                <IconItem icon={<IoDownloadOutline />} title="求人票PDFのダウンロード">
                  求人票をPDFで保存できます。複数件まとめて一括ダウンロードすることも可能です（操作は下記）。
                </IconItem>
                <IconItem icon={<IoSwapVerticalOutline />} title="店舗数・卒業生数で並び替え">
                  一覧の見出しの「店舗数」「卒業生」の横にある並び替えボタンを押すと、多い順に並び替わります。
                  もう一度押すと元の順に戻ります（推奨の印をつけた求人は、並び替えても上に表示されます）。
                </IconItem>
                <IconItem icon={<FaRegStar />} title="推奨（星マーク）">
                  求人に「推奨」の印をつけられます。学校と連携している学生のアプリ上にも「推奨」として表示される
                  ので、特に学生におすすめしたい求人の目印としてお使いください（操作は下記）。
                </IconItem>
                <IconItem icon={<RiLockPasswordLine />} title="ブロック（鍵マーク）">
                  求人をブロックできます。ブロックすると、学校と連携している学生のアプリにその求人が表示され
                  なくなります（操作は下記）。
                </IconItem>
                {SHOW_SCAN_IMPORT && (
                  <IconItem icon={<IoDocumentTextOutline />} title="紙の求人票の取り込み（スキャン）">
                    サロンから紙で届いた求人票は、「取り込み」からファイルをドラッグ&ドロップしてデジタル化
                    できます。この取り込みは専用の保管先に保存されるだけで、既存の求人一覧（公開されている求人情報）
                    には影響しません。
                  </IconItem>
                )}
              </FeatureBox>

              <SubHeading>求人票PDFを印刷・ダウンロードする</SubHeading>
              <StepList>
                <Step>印刷したい求人のチェックボックスを選びます（複数選択できます）。</Step>
                <Step>「求人票を印刷する」ボタンを押します。</Step>
              </StepList>
              <Shot
                src="/school-manual/recruit-print.png"
                alt="求人票の印刷画面"
                hotspots={[
                  { n: 1, x: 12, y: 37 },
                  { n: 2, x: 85, y: 16 },
                ]} />

              <SubHeading>推奨・ブロックの場所</SubHeading>
              <StepList>
                <Step>
                  星マークをクリックすると、その求人に「推奨」の印がつきます。学校と連携している学生の
                  アプリ上にも「推奨」として表示され、学校がすすめている求人だと伝わります。
                </Step>
                <Step>
                  鍵マークをクリックすると、確認画面のあと、その求人をブロックできます。ブロックすると、
                  学校と連携している学生のアプリにその求人が表示されなくなります。
                </Step>
              </StepList>
              <Shot
                src="/school-manual/recruit-list.png"
                alt="求人情報タブの一覧画面(推奨・ブロックの位置)"
                hotspots={[
                  { n: 1, x: 81, y: 37 },
                  { n: 2, x: 85, y: 37 },
                ]} />

              <TipBox>
                <li>求人票を一括ダウンロードしておけば、進路指導室への掲示や配布資料としてまとめて印刷する運用に使えます。</li>
                <li>特に推したいサロンに星マークを付けておくと、学生に声をかけるときの目印として使えます。</li>
                <li>都道府県や職種で絞り込んでおくと、地元志向の学生や特定分野を希望する学生への案内がしやすくなります。</li>
              </TipBox>
            </section>

            <section
              id="students"
              ref={(el) => (sectionRefs.current['students'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoPeopleOutline className={Styles.sectionIcon} />
                学生の進捗情報
              </h2>
              <SectionLead>学生一人ひとり、またはクラス全体の就職活動の進み具合を確認できるタブです。</SectionLead>

              <StepList>
                <Step>画面上部の「コース」「学年」「クラス」をすべて選びます（3つとも選ぶまで一覧は表示されません）。</Step>
                <Step>学生ごとの一覧が表示されます。右上の「表示切替」を押すと、クラス単位のグラフ表示に切り替わります。</Step>
              </StepList>
              <Shot
                src="/school-manual/students-empty.png"
                alt="コース・学年・クラス未選択時の学生の進捗情報タブ"
                hotspots={[
                  { n: 1, x: 56, y: 16 },
                  { n: 1, x: 70, y: 16 },
                  { n: 1, x: 85, y: 16 },
                  { n: 2, x: 86, y: 7 },
                ]} />
              <div className={Styles.shotRow}>
                <Shot
                  src="/school-manual/students-filtered.png"
                  alt="クラスを絞り込んだあとの学生の進捗情報一覧"
                  caption="学生ごとに作品数・閲覧数・見学数・志望先数・応募中・内定承諾などが並ぶ" />
                <Shot
                  src="/school-manual/students-graph.png"
                  alt="学生の進捗情報タブのグラフ表示"
                  caption="選んだクラス全体の閲覧数・お気に入り・サロン見学・志望先・応募済み・内定承諾の合計を棒グラフで確認できる" />
              </div>

              <TipBox>
                <li>一覧の閲覧数・見学数が少ない学生を見つけて、個別に声をかけるきっかけとして使えます。</li>
                <li>グラフ表示でクラスごとの合計を見比べると、他のクラスより活動が遅れているクラスを把握できます。</li>
                <li>内定承諾の件数を定期的に確認し、まだ決まっていない学生へのフォローの優先順位づけに使えます。</li>
              </TipBox>
            </section>

            <section
              id="salon"
              ref={(el) => (sectionRefs.current['salon'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoStorefrontOutline className={Styles.sectionIcon} />
                サロン分析
              </h2>
              <SectionLead>学生が見学・応募しているサロンを、企業ごとに一覧できるタブです。</SectionLead>
              <p>
                どのサロンにどれくらいの学生が興味を持っているかを把握する際にご利用ください。
              </p>

              <StepList>
                <Step>「学生の進捗情報」と同じく、画面上部の「コース」「学年」「クラス」をすべて選びます。</Step>
                <Step>選んだクラスの学生に基づいて、企業ごとの集計が表示されます。</Step>
              </StepList>
              <Shot
                src="/school-manual/salon-analysis-empty.png"
                alt="コース・学年・クラス未選択時のサロン分析タブ"
                hotspots={[
                  { n: 1, x: 56, y: 16 },
                  { n: 1, x: 70, y: 16 },
                  { n: 1, x: 85, y: 16 },
                ]} />
              <Shot
                src="/school-manual/salon-analysis.png"
                alt="サロン分析タブの一覧画面"
                caption="企業ごとに閲覧数・サロン見学・志望先・応募済み・内定承諾・辞退・不合格の件数が並ぶ" />

              <TipBox>
                <li>見学・応募が集中しているサロンを把握しておくと、学生からの関心が高い企業を優先して案内できます。</li>
                <li>「辞退」「不合格」が多いサロンがあれば、学生に選考前のアドバイスをする際の参考にできます。</li>
              </TipBox>
            </section>

            <section
              id="guidance"
              ref={(el) => (sectionRefs.current['guidance'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoChatbubblesOutline className={Styles.sectionIcon} />
                ガイダンス回答状況
              </h2>
              <SectionLead>学校説明会（ガイダンス）で学生が回答した志望状況を確認できるタブです。</SectionLead>
              <p>
                学校によって回答形式が異なり、「第1志望群・第2志望群」で分ける学校と、「第1位〜第N位」まで
                順位をつける学校があります。
              </p>
              <p>
                このタブは、該当する学校（ガイダンス機能を利用している学校）にのみ表示されます。学生がアプリ上で
                回答した内容と、Webの回答フォームから送られた内容の両方をまとめて確認できます。
              </p>
              <p>
                回答状況は「未回答」（赤）・「回答済」（緑）・「内定済」（金）の3段階で表示されます。一覧は「未回答」→「内定済」→「回答済」の順に並びます。
                「内定済」は、学生が内定を得て報告した状態で、この段階になると志望群の内容は一覧・詳細のどちらにも
                表示されなくなります。
              </p>
              <StepList>
                <Step>一覧で学生ごとの回答状況（未回答・回答済・内定済）を確認します。</Step>
                <Step>「詳細」を押すと、その学生1人分の回答内容を確認できます（未回答の行は押せません）。</Step>
              </StepList>
              <Shot
                src="/school-manual/guidance-status.png"
                alt="ガイダンス回答状況タブの一覧画面"
                caption="第1志望群〜第2志望群(または順位)が、回答済・内定済の行に並ぶ"
                hotspots={[
                  { n: 1, x: 27, y: 73 },
                  { n: 2, x: 86, y: 73 },
                ]} />
              <Shot
                src="/school-manual/guidance-detail.png"
                alt="ガイダンス回答の詳細画面"
                caption="第1志望群・第2志望群ごとに志望サロンの都道府県や、求人票・パンフレット・求人情報へのリンクを確認できる" />

              <TipBox>
                <li>「未回答」の学生を一覧で確認し、回答期限が近づいたタイミングで個別に声をかけるのに使えます。</li>
                <li>「内定済」になると志望群の中身は見えなくなるため、内定後のフォローは「学生の進捗情報」タブと
                  あわせて確認してください。</li>
              </TipBox>
            </section>

            <section
              id="absence"
              ref={(el) => (sectionRefs.current['absence'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoCalendarOutline className={Styles.sectionIcon} />
                公欠申請
              </h2>
              <SectionLead>学生がアプリから提出した公欠申請を確認し、許可・不許可を決めるタブです。</SectionLead>
              <p>
                学生がアプリの「設定 → 就活ツール → 公欠申請」から、自校宛てに提出した公欠申請を確認・処理する
                タブです。このタブは、公欠申請の機能を利用している学校にのみ表示されます。
              </p>
              <p>申請の種類は次の4つです。</p>
              <ul className={Styles.list}>
                <li>サロン見学</li>
                <li>会社説明会</li>
                <li>面接・選考</li>
                <li>その他（学生が入力した内容が添えて表示されます）</li>
              </ul>

              <SubHeading>申請を許可・不許可にする</SubHeading>
              <StepList>
                <Step>「許可待ち」タブで、処理する申請を探します。</Step>
                <Step>「詳細」を押して、申請種別・実施日・時間・場所を確認します。</Step>
                <Step>内容に問題がなければ○（許可）、認められない場合は×（不許可）を押します。</Step>
              </StepList>
              <Shot
                src="/school-manual/absence-requests.png"
                alt="公欠申請タブの一覧画面(許可待ち)"
                hotspots={[
                  { n: 1, x: 14, y: 24 },
                  { n: 2, x: 77, y: 37 },
                  { n: 3, x: 84, y: 37 },
                ]} />
              <div className={Styles.notice}>
                <strong>重要：</strong>
                ○（許可）・×（不許可）のボタンは、押すと確認画面なしでただちに保存されます。
                ○から×、×から○へ直接切り替えることはできません（いったん下の手順で許可待ちに戻してから、
                あらためて押し直してください）。
              </div>

              <SubHeading>許可・不許可をやり直す</SubHeading>
              <StepList>
                <Step>「許可済み」または「不許可」タブを開きます。選ばなかったほうのボタンの位置がリセットアイコン（↻）になっています。</Step>
                <Step>リセットアイコン（↻）を押すと、「許可待ち」に戻ります。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/school-manual/absence-approved.png"
                  alt="公欠申請タブの許可済み一覧画面"
                  caption="許可済みタブ。○の位置に色がついたまま残る"
                  hotspots={[{ n: 2, x: 86, y: 37 }]} />
                <Shot
                  src="/school-manual/absence-rejected.png"
                  alt="公欠申請タブの不許可一覧画面"
                  caption="不許可タブ。×の位置に色がついたまま残る"
                  hotspots={[{ n: 2, x: 82, y: 37 }]} />
              </div>
              <Shot
                src="/school-manual/absence-detail.png"
                alt="公欠申請の詳細画面"
                caption="サロン情報、許可の状況（状態・申請日、決定後は許可日／不許可日）も確認できる" />

              <TipBox>
                <li>「許可待ち」タブの件数バッジを定期的にチェックし、申請が溜まったままにならないよう処理してください。</li>
                <li>許可・不許可を押す前に「詳細」で日時・場所を必ず確認してください。押し間違えてもリセット
                  アイコン（↻）で「許可待ち」に戻せます。</li>
              </TipBox>
            </section>

            <section
              id="faq"
              ref={(el) => (sectionRefs.current['faq'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHelpCircleOutline className={Styles.sectionIcon} />
                よくある質問
              </h2>
              <SectionLead>自分で解決できそうにない場合は、運営（memorii works）まで、画面のスクリーンショットを添えてお問い合わせください。</SectionLead>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. ログインできません。</p>
                <p className={Styles.faqA}>
                  A. ログイン画面の「アカウント種別」が「学校関係者様」になっているかご確認ください。
                  「事業者様」のままだとログインできません。メールアドレス・パスワードに誤りがない場合は、
                  運営（memorii works）までお問い合わせください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 一部のタブ（学生の進捗情報・サロン分析・ガイダンス回答状況・公欠申請）が表示されません。</p>
                <p className={Styles.faqA}>
                  A. これらのタブは契約内容や機能の利用状況によって表示が変わります。表示されるべきタブが
                  見当たらない場合は、運営（memorii works）までお問い合わせください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 求人票の内容が空欄に見えることがあります。</p>
                <p className={Styles.faqA}>
                  A. サロン側の登録内容に不備がある場合に起こることがあります。気になる求人がある場合は、
                  求人名・サロン名を控えて運営までお知らせください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 公欠申請を間違って許可（不許可）してしまいました。</p>
                <p className={Styles.faqA}>
                  A. 「許可済み」「不許可」タブに出ているリセットアイコン（↻）を押すと「許可待ち」に戻せます。
                  許可待ちに戻ったら、あらためて正しいほうを選んでください。
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* ページ上部へ戻る(ある程度スクロールしたときだけ表示) */}
      {showBackToTop && (
        <button
          type="button"
          className={Styles.backToTop}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="ページ上部へ戻る">
          <IoArrowUpOutline />
        </button>
      )}

      {/* 画像の拡大表示(クリックで開く。背景クリック・✕・Escで閉じる) */}
      {zoom && (
        <div className={Styles.lightbox} role="dialog" aria-modal="true" aria-label={zoom.alt}>
          <div className={Styles.lightboxBg} onClick={() => setZoom(null)} />
          <button type="button" className={Styles.lightboxClose} onClick={() => setZoom(null)} aria-label="閉じる">
            <IoCloseOutline />
          </button>
          <img className={Styles.lightboxImg} src={zoom.src} alt={zoom.alt} />
        </div>
      )}
    </ZoomContext.Provider>
  );
};

export default SchoolManual;
